import { useEffect, useRef, useState } from 'react';
import Layout from '../components/Layout';
import AddIcon from '@mui/icons-material/Add';
import PageHeader from './PageHeader';
import {
    Box,
    Button,
    Drawer,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography,
    IconButton,
    TextField,
    Autocomplete,
    CircularProgress,
    Popover,
    TablePagination,
    Stack,
    MenuItem,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import QuickViewDrawer from './sub-components/QuickViewDrawer';
import CustomerService from '../services/CustomerService';
import QuoteService from '../services/QuoteService';
import { useNavigate } from 'react-router-dom';
import ConfirmModal from './modal/ConfirmModal';
import { useToast } from '../contexts/ToastContext';
import { useTranslation } from 'react-i18next';

const ACCENT = '#427898';

const quoteStatusOptions = [
    { code: 'DRAFT',                       color: '#9E9E9E' },
    { code: 'SENT',                        color: '#4A90E2' },
    { code: 'AWAITING_SIGNATURE',          color: '#F4A261' },
    { code: 'AWAITING_COMPANY_SIGNATURE',  color: '#F4A261' },
    { code: 'COMPANY_SIGNED',              color: '#7B8FA1' },
    { code: 'AWAITING_CUSTOMER_SIGNATURE', color: '#F4A261' },
    { code: 'SIGNED',                      color: '#2EC4B6' },
    { code: 'ACCEPTED',                    color: '#7DCFB6' },
    { code: 'REJECTED',                    color: '#E07A5F' },
    { code: 'CANCELLED',                   color: '#B0B0B0' },
    { code: 'EXPIRED',                     color: '#F4C067' },
];

const statusColor = (code) => quoteStatusOptions.find(s => s.code === code)?.color || '#9E9E9E';

// Filter pills group the 11 statuses into a scannable set
const SIGNING_STATUSES = ['AWAITING_SIGNATURE', 'AWAITING_COMPANY_SIGNATURE', 'COMPANY_SIGNED', 'AWAITING_CUSTOMER_SIGNATURE'];
const FILTER_GROUPS = [
    { id: 'DRAFT',    statuses: ['DRAFT'],                              color: '#9E9E9E' },
    { id: 'SENT',     statuses: ['SENT'],                               color: '#4A90E2' },
    { id: 'SIGNING',  statuses: SIGNING_STATUSES,                       color: '#F4A261' },
    { id: 'SIGNED',   statuses: ['SIGNED'],                             color: '#2EC4B6' },
    { id: 'ACCEPTED', statuses: ['ACCEPTED'],                           color: '#7DCFB6' },
    { id: 'CLOSED',   statuses: ['REJECTED', 'CANCELLED', 'EXPIRED'],   color: '#B0B0B0' },
];

const fmtAmount = (v) => v != null ? `CHF ${Number(v).toLocaleString('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—';
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('de-CH') : '—';

function FilterPill({ label, count, active, onClick, color }) {
    return (
        <Box
            onClick={onClick}
            sx={{
                px: 1.5, py: 0.5, borderRadius: '20px', cursor: 'pointer',
                display: 'inline-flex', alignItems: 'center', gap: 0.75,
                fontSize: 13, fontWeight: active ? 700 : 500,
                bgcolor: active ? (color || ACCENT) : 'transparent',
                color: active ? '#fff' : '#64748B',
                border: '1.5px solid',
                borderColor: active ? (color || ACCENT) : '#E2E8F0',
                userSelect: 'none', whiteSpace: 'nowrap',
                transition: 'all 0.12s',
                '&:hover': { borderColor: color || ACCENT, color: active ? '#fff' : (color || ACCENT), bgcolor: active ? (color || ACCENT) : `${color || ACCENT}10` },
            }}
        >
            {label}
            {count != null && (
                <Box component="span" sx={{ fontSize: 11, fontWeight: 700, opacity: active ? 0.85 : 0.6 }}>{count}</Box>
            )}
        </Box>
    );
}

function StatusPill({ code, label }) {
    const color = statusColor(code);
    return (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
            <Typography sx={{ fontSize: 13, fontWeight: 600, color }}>{label}</Typography>
        </Box>
    );
}

function StatCard({ label, value, sub }) {
    return (
        <Paper elevation={0} sx={{ border: '1px solid #EEF2F6', borderRadius: 2, px: 2.5, py: 1.75, minWidth: 170 }}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 1 }}>{label}</Typography>
            <Typography sx={{ fontSize: 20, fontWeight: 700, color: '#0F172A', mt: 0.5, lineHeight: 1.2 }}>{value}</Typography>
            {sub && <Typography sx={{ fontSize: 12, color: '#94A3B8', mt: 0.25 }}>{sub}</Typography>}
        </Paper>
    );
}

const emptyForm = {
    customerId: '',
    customerContactId: '',
    quoteDate: new Date().toISOString().substring(0, 10),
    validUntil: new Date(Date.now() + 30 * 864e5).toISOString().substring(0, 10),
    taxRate: 8.1,
    notes: '',
};

const QuotesPage = () => {
    const navigate = useNavigate();
    const { showToast } = useToast();
    const { t } = useTranslation();

    const [loading, setLoading] = useState(false);
    const [quotes, setQuotes] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchText, setSearchText] = useState('');
    const [statusFilter, setStatusFilter] = useState([]);

    const [customers, setCustomers] = useState([]);
    const [customerContacts, setCustomerContacts] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [selectedContact, setSelectedContact] = useState(null);

    const [modalOpen, setModalOpen] = useState(false);
    const [editingQuote, setEditingQuote] = useState(null);
    const [formData, setFormData] = useState(emptyForm);

    const [anchorEl, setAnchorEl] = useState(null);
    const [activeQuote, setActiveQuote] = useState(null);

    const [stats, setStats] = useState(null);
    const [quickView, setQuickView] = useState(null);

    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const searchTimer = useRef(null);

    // ── Fetch ──────────────────────────────────────────────────────────────────

    const fetchQuotes = async () => {
        setLoading(true);
        try {
            const statuses = statusFilter.flatMap(id => FILTER_GROUPS.find(g => g.id === id)?.statuses ?? []);
            let data;
            if (searchText || statuses.length > 0) {
                data = await QuoteService.searchQuotes(page, rowsPerPage, {
                    searchText,
                    statuses: statuses.length > 0 ? statuses : undefined,
                });
            } else {
                data = await QuoteService.getQuotes(page, rowsPerPage);
            }
            setQuotes(data.content || []);
            setTotal(data.totalElements ?? 0);
        } catch {
            showToast(t('quotes.messages.error'), 'error');
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = () => QuoteService.getStats().then(setStats).catch(() => {});

    useEffect(() => { fetchStats(); }, []);

    const fetchCustomers = async () => {
        try {
            const data = await CustomerService.getCustomers(0, 200);
            setCustomers(data.content || []);
        } catch {
            /* silent */
        }
    };

    useEffect(() => { fetchCustomers(); }, []);
    useEffect(() => { fetchQuotes(); }, [page, rowsPerPage, statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

    // debounced search
    useEffect(() => {
        clearTimeout(searchTimer.current);
        searchTimer.current = setTimeout(() => {
            setPage(0);
            fetchQuotes();
        }, 400);
        return () => clearTimeout(searchTimer.current);
    }, [searchText]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!selectedCustomer) { setCustomerContacts([]); setSelectedContact(null); return; }
        CustomerService.getCustomer(selectedCustomer.id).then(data => {
            setCustomerContacts(data.customerContact || []);
        }).catch(() => setCustomerContacts([]));
    }, [selectedCustomer]);

    // ── Modal ─────────────────────────────────────────────────────────────────

    const openCreate = () => {
        setEditingQuote(null);
        setFormData(emptyForm);
        setSelectedCustomer(null);
        setSelectedContact(null);
        setModalOpen(true);
    };

    const openEdit = (quote) => {
        setEditingQuote(quote);
        setFormData({
            customerId: quote.customerId,
            customerContactId: quote.customerContactId || '',
            quoteDate: quote.quoteDate || new Date().toISOString().substring(0, 10),
            validUntil: quote.validUntil || '',
            taxRate: quote.taxRate ?? 8.1,
            notes: quote.notes || '',
        });
        const cust = customers.find(c => c.id === quote.customerId) || null;
        setSelectedCustomer(cust);
        setModalOpen(true);
    };

    const closeModal = () => {
        setModalOpen(false);
        setEditingQuote(null);
        setSelectedCustomer(null);
        setSelectedContact(null);
    };

    const handleSave = async () => {
        if (!formData.customerId) { showToast(t('quotes.validation.customerRequired'), 'warning'); return; }
        if (!formData.validUntil) { showToast(t('quotes.validation.validUntilRequired'), 'warning'); return; }
        setIsSaving(true);
        try {
            if (editingQuote) {
                await QuoteService.updateQuote(editingQuote.id, formData);
                showToast(t('quotes.messages.updated'), 'success');
            } else {
                await QuoteService.createQuote(formData);
                showToast(t('quotes.messages.created'), 'success');
            }
            closeModal();
            fetchQuotes();
            fetchStats();
        } catch {
            showToast(t('quotes.messages.error'), 'error');
        } finally {
            setIsSaving(false);
        }
    };

    // ── Delete ────────────────────────────────────────────────────────────────

    const confirmDelete = (quote) => {
        setActiveQuote(quote);
        setAnchorEl(null);
        setConfirmDeleteOpen(true);
    };

    const handleDelete = async () => {
        if (!activeQuote) return;
        setDeleting(true);
        try {
            await QuoteService.deleteQuote(activeQuote.id);
            showToast(t('quotes.messages.deleted'), 'success');
            setConfirmDeleteOpen(false);
            fetchQuotes();
            fetchStats();
        } catch {
            showToast(t('quotes.messages.error'), 'error');
        } finally {
            setDeleting(false);
        }
    };

    // ── Popover ───────────────────────────────────────────────────────────────

    const handleMoreClick = (e, quote) => { setAnchorEl(e.currentTarget); setActiveQuote(quote); };
    const handleMoreClose = () => setAnchorEl(null);

    // ── Stats & filter helpers ────────────────────────────────────────────────

    const counts = stats?.countsByStatus ?? {};
    const groupCount = (g) => g.statuses.reduce((a, s) => a + (counts[s] ?? 0), 0);
    const openCount = ['SENT', ...SIGNING_STATUSES].reduce((a, s) => a + (counts[s] ?? 0), 0);
    const awaitingCount = SIGNING_STATUSES.filter(s => s.startsWith('AWAITING')).reduce((a, s) => a + (counts[s] ?? 0), 0);

    const statusLabelT = (code) => t(`quotes.statuses.${code}`, { defaultValue: code });
    const groupLabel = (g) => {
        if (g.id === 'SIGNING') return t('quotes.filters.signing', { defaultValue: 'In Signing' });
        if (g.id === 'CLOSED') return t('quotes.filters.closed', { defaultValue: 'Closed' });
        return statusLabelT(g.id);
    };

    const toggleGroup = (id) => {
        setPage(0);
        setStatusFilter(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    return (
        <Layout>
            <PageHeader
                title={t('quotes.title')}
                count={total || null}
                action={
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={openCreate}
                        sx={{ bgcolor: '#427898', textTransform: 'none' }}
                    >
                        {t('quotes.add')}
                    </Button>
                }
            />
            <Box sx={{ p: 3 }}>
                {/* Stats strip */}
                {stats && (
                    <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                        <StatCard
                            label={t('quotes.stats.openValue', { defaultValue: 'Open volume' })}
                            value={fmtAmount(stats.openValue)}
                            sub={`${openCount} ${t('quotes.stats.openQuotes', { defaultValue: 'open quotes' })}`}
                        />
                        <StatCard
                            label={t('quotes.stats.awaitingSignature', { defaultValue: 'Awaiting signature' })}
                            value={awaitingCount}
                        />
                        <StatCard
                            label={t('quotes.stats.expiringSoon', { defaultValue: 'Expiring ≤ 14 days' })}
                            value={stats.expiringSoon ?? 0}
                        />
                    </Box>
                )}

                {/* Filter bar */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#fff', border: '1px solid #E2E8F0', borderRadius: '12px', px: 2, py: 1.25, mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflow: 'auto', flex: 1, scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
                        <FilterPill
                            label={t('common.all', { defaultValue: 'All' })}
                            active={statusFilter.length === 0}
                            onClick={() => { setStatusFilter([]); setPage(0); }}
                        />
                        {FILTER_GROUPS.map(g => (
                            <FilterPill
                                key={g.id}
                                label={groupLabel(g)}
                                count={stats ? groupCount(g) : null}
                                active={statusFilter.includes(g.id)}
                                onClick={() => toggleGroup(g.id)}
                                color={g.color}
                            />
                        ))}
                    </Box>
                    <TextField
                        size="small"
                        placeholder={t('quotes.search')}
                        value={searchText}
                        onChange={e => setSearchText(e.target.value)}
                        InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: '#aaa', fontSize: 20 }} /> }}
                        sx={{ width: 240, flexShrink: 0, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                    />
                </Box>

                {/* Table */}
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
                        <CircularProgress sx={{ color: ACCENT }} />
                    </Box>
                ) : quotes.length === 0 ? (
                    <Box sx={{ border: '1px solid #E2E8F0', borderRadius: '12px', bgcolor: '#fff', py: 8, textAlign: 'center' }}>
                        <RequestQuoteIcon sx={{ fontSize: 40, color: '#CBD5E1', mb: 1.5 }} />
                        <Typography fontWeight={600} color="#475569">{t('quotes.empty')}</Typography>
                    </Box>
                ) : (
                    <Paper elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: '12px', overflow: 'hidden' }}>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                                        {[t('quotes.number'), t('quotes.customer'), t('quotes.status'), t('quotes.validUntil'), t('quotes.total'), ''].map((h, i) => (
                                            <TableCell
                                                key={i}
                                                align={i === 4 ? 'right' : 'left'}
                                                sx={{ fontWeight: 700, fontSize: 11, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.6, py: 1.5, ...(i === 5 ? { width: 88, p: 0 } : {}) }}
                                            >
                                                {h}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {quotes.map(q => (
                                        <TableRow
                                            key={q.id}
                                            onClick={() => navigate(`/quote-info/${q.id}`)}
                                            sx={{
                                                cursor: 'pointer',
                                                '&:last-child td': { border: 0 },
                                                '&:hover': { bgcolor: '#F8FAFC' },
                                                '&:hover .row-actions': { opacity: 1 },
                                                '& .row-actions': { opacity: 0, transition: 'opacity 0.15s' },
                                            }}
                                        >
                                            <TableCell sx={{ py: 1.25 }}>
                                                <Typography sx={{ fontWeight: 700, color: '#0F172A', fontSize: 13 }}>{q.displayName}</Typography>
                                                <Typography sx={{ fontSize: 12, color: '#94A3B8' }}>{fmtDate(q.quoteDate)}</Typography>
                                            </TableCell>
                                            <TableCell sx={{ fontSize: 13, color: '#475569' }}>{q.customerName}</TableCell>
                                            <TableCell><StatusPill code={q.status} label={statusLabelT(q.status)} /></TableCell>
                                            <TableCell sx={{ fontSize: 13, color: q.status === 'EXPIRED' ? '#ef4444' : '#64748B', fontWeight: q.status === 'EXPIRED' ? 700 : 400 }}>
                                                {fmtDate(q.validUntil)}
                                            </TableCell>
                                            <TableCell align="right" sx={{ fontSize: 13, fontWeight: 700, color: '#0F172A' }}>{fmtAmount(q.totalAmount)}</TableCell>
                                            <TableCell sx={{ p: 0, pr: 1 }} onClick={e => e.stopPropagation()}>
                                                <Box className="row-actions" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.25 }}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => setQuickView(q)}
                                                        sx={{ color: '#94A3B8', '&:hover': { color: ACCENT, bgcolor: '#F0F7FF' } }}
                                                    >
                                                        <VisibilityOutlinedIcon sx={{ fontSize: 17 }} />
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        onClick={e => handleMoreClick(e, q)}
                                                        sx={{ color: '#94A3B8', '&:hover': { color: ACCENT, bgcolor: '#F0F7FF' } }}
                                                    >
                                                        <MoreHorizIcon sx={{ fontSize: 18 }} />
                                                    </IconButton>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            component="div"
                            count={total}
                            page={page}
                            onPageChange={(_, p) => setPage(p)}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                            rowsPerPageOptions={[10, 25, 50]}
                            sx={{ borderTop: '1px solid #F1F5F9' }}
                        />
                    </Paper>
                )}

                {/* Quick view */}
                <QuickViewDrawer
                    open={Boolean(quickView)}
                    onClose={() => setQuickView(null)}
                    title={quickView?.displayName}
                    statusColor={statusColor(quickView?.status)}
                    statusLabel={quickView ? statusLabelT(quickView.status) : null}
                    facts={quickView ? [
                        { label: t('quotes.customer'), value: quickView.customerName },
                        { label: t('quotes.total'), value: fmtAmount(quickView.totalAmount) },
                        { label: t('quotes.quoteDate'), value: fmtDate(quickView.quoteDate) },
                        { label: t('quotes.validUntil'), value: fmtDate(quickView.validUntil) },
                    ] : []}
                    loadPdf={() => QuoteService.downloadQuote(quickView.id)}
                    onOpenFull={() => navigate(`/quote-info/${quickView.id}`)}
                    openLabel={t('quotes.quickView.open', { defaultValue: 'Open quote' })}
                    pdfErrorLabel={t('common.pdfPreviewUnavailable', { defaultValue: 'PDF preview not available' })}
                />

                {/* Row action popover */}
                <Popover
                    open={Boolean(anchorEl)}
                    anchorEl={anchorEl}
                    onClose={handleMoreClose}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                    <Box sx={{ py: 0.5 }}>
                        <MenuItem onClick={() => { navigate(`/quote-info/${activeQuote?.id}`); handleMoreClose(); }}>
                            {t('buttons.view')}
                        </MenuItem>
                        {activeQuote?.status === 'DRAFT' && (
                            <MenuItem onClick={() => { openEdit(activeQuote); handleMoreClose(); }}>
                                {t('quotes.edit')}
                            </MenuItem>
                        )}
                        <MenuItem sx={{ color: 'error.main' }} onClick={() => confirmDelete(activeQuote)}>
                            {t('quotes.delete')}
                        </MenuItem>
                    </Box>
                </Popover>

                {/* Create/Edit drawer */}
                <Drawer
                    anchor="right"
                    open={modalOpen}
                    onClose={closeModal}
                    PaperProps={{ sx: { width: { xs: '100vw', sm: 520 }, display: 'flex', flexDirection: 'column' } }}
                >
                    <Box sx={{ height: 3, bgcolor: '#427898', flexShrink: 0 }} />
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, py: 2, borderBottom: '1px solid #e2e8f0', flexShrink: 0 }}>
                        <Box>
                            <Typography variant="h6" fontWeight={700} sx={{ color: '#1a2332' }}>
                                {editingQuote ? t('quotes.info.editTitle') : t('quotes.add')}
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#7f8c9a', mt: 0.25 }}>
                                {editingQuote
                                    ? t('quotes.editSubtitle', { defaultValue: 'Update the quote details below' })
                                    : t('quotes.createSubtitle', { defaultValue: 'Fill in the details to create a new quote' })}
                            </Typography>
                        </Box>
                        <IconButton size="small" onClick={closeModal} sx={{ color: '#64748b' }}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>

                    <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 3 }}>
                        <Stack spacing={2.5}>
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                                <Autocomplete
                                    options={customers}
                                    getOptionLabel={c => c.name || ''}
                                    value={selectedCustomer}
                                    onChange={(_, val) => {
                                        setSelectedCustomer(val);
                                        setFormData(f => ({ ...f, customerId: val?.id || '' }));
                                    }}
                                    renderInput={p => (
                                        <TextField {...p} label={`${t('quotes.customer')} *`} size="small" />
                                    )}
                                    isOptionEqualToValue={(a, b) => a.id === b.id}
                                />
                                <Autocomplete
                                    options={customerContacts}
                                    getOptionLabel={c => c.firstName + ' ' + c.lastName}
                                    value={selectedContact}
                                    onChange={(_, val) => {
                                        setSelectedContact(val);
                                        setFormData(f => ({ ...f, customerContactId: val?.id || '' }));
                                    }}
                                    disabled={!selectedCustomer}
                                    renderInput={p => (
                                        <TextField {...p} label={t('quotes.contact')} size="small" />
                                    )}
                                    isOptionEqualToValue={(a, b) => a.id === b.id}
                                />
                            </Box>
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                                <TextField
                                    label={`${t('quotes.quoteDate')} *`}
                                    type="date"
                                    size="small"
                                    value={formData.quoteDate}
                                    onChange={e => setFormData(f => ({ ...f, quoteDate: e.target.value }))}
                                    InputLabelProps={{ shrink: true }}
                                    fullWidth
                                />
                                <TextField
                                    label={`${t('quotes.validUntil')} *`}
                                    type="date"
                                    size="small"
                                    value={formData.validUntil}
                                    onChange={e => setFormData(f => ({ ...f, validUntil: e.target.value }))}
                                    InputLabelProps={{ shrink: true }}
                                    fullWidth
                                />
                            </Box>
                            <TextField
                                label={t('quotes.taxRate')}
                                type="number"
                                size="small"
                                value={formData.taxRate}
                                onChange={e => setFormData(f => ({ ...f, taxRate: parseFloat(e.target.value) || 0 }))}
                                sx={{ maxWidth: 200 }}
                                inputProps={{ step: 0.1, min: 0 }}
                            />
                            <TextField
                                label={t('quotes.notes')}
                                multiline
                                minRows={3}
                                size="small"
                                value={formData.notes}
                                onChange={e => setFormData(f => ({ ...f, notes: e.target.value }))}
                                fullWidth
                            />
                        </Stack>
                    </Box>

                    <Box sx={{ borderTop: '1px solid #e2e8f0', px: 3, py: 2, display: 'flex', justifyContent: 'flex-end', gap: 1, flexShrink: 0 }}>
                        <Button onClick={closeModal} sx={{ color: '#64748b', textTransform: 'none' }}>
                            {t('common.cancel')}
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleSave}
                            disabled={isSaving}
                            sx={{ bgcolor: '#427898', textTransform: 'none' }}
                        >
                            {isSaving
                                ? <CircularProgress size={18} sx={{ color: '#fff' }} />
                                : (editingQuote ? t('common.save') : t('quotes.addItem'))}
                        </Button>
                    </Box>
                </Drawer>

                {/* Delete confirm */}
                <ConfirmModal
                    open={confirmDeleteOpen}
                    onClose={() => setConfirmDeleteOpen(false)}
                    onConfirm={handleDelete}
                    isLoading={deleting}
                    question={t('quotes.info.confirmDeleteQuote', { name: activeQuote?.displayName })}
                />
            </Box>
        </Layout>
    );
};

export default QuotesPage;
